import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AppSidebar,
  Banner,
  BottomNav,
  Button,
  ConfirmDialog,
  HStack,
  IconButton,
  Text,
  VStack,
  type NavSection,
  useIsMobile,
} from 'wss3-forge'
import {
  Brain20Regular,
  Chat20Regular,
  ChatAdd20Regular,
  ChevronDown20Regular,
  ChevronUp20Regular,
  Delete16Regular,
  History20Regular,
  Laptop20Regular,
  Navigation20Regular,
  PlugConnected20Regular,
  Pulse20Regular,
  TaskListSquareLtr20Regular,
} from '@fluentui/react-icons'
import { useIncognito } from '../contexts/IncognitoContext.js'
import { UserMenu } from './UserMenu.js'
import { useRecentSessions } from '../hooks/useRecentSessions.js'
import { useChatContext } from '../contexts/ChatContext.js'
import { cleanSessionTitle } from '../lib/session-title.js'
import { deleteSession, type SessionSummary } from '../lib/api.js'

interface AppShellProps {
  children: ReactNode
}

const NAV_PATHS: Record<string, string> = {
  memories: '/memories',
  tasks: '/tasks',
  sessions: '/sessions',
  connecteurs: '/connecteurs',
  devices: '/devices',
  traces: '/traces',
}

const COLLAPSED_KEY = 'stark.sidebar.collapsed'

function loadCollapsed(): boolean {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(COLLAPSED_KEY) === '1'
}

function activeIdFromPath(pathname: string): string {
  if (pathname === '/') return 'new-chat'
  if (pathname.startsWith('/sessions')) return 'sessions'
  if (pathname.startsWith('/memories')) return 'memories'
  if (pathname.startsWith('/tasks')) return 'tasks'
  if (pathname.startsWith('/connecteurs')) return 'connecteurs'
  if (pathname.startsWith('/devices')) return 'devices'
  if (pathname.startsWith('/traces')) return 'traces'
  return 'new-chat'
}

function HeaderBrand() {
  return (
    <img
      src="/logo.svg"
      alt="Stark"
      height={40}
      style={{ display: 'block', height: 40, width: 'auto' }}
    />
  )
}

/**
 * Rendered in place of `HeaderBrand` when the sidebar is collapsed to its
 * 60px icon rail. The collapsed sidebar header lives in a flex column with
 * 32px of horizontal padding leftover, so we draw an explicit 32x32 brand
 * tile rather than a Text glyph: the tile guarantees a visible, centered
 * brand anchor regardless of any Text component default alignment quirks.
 */
function CompactBrand() {
  return (
    <div
      aria-label="Stark"
      style={{
        width: 32,
        height: 32,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'color-mix(in srgb, var(--brand-primary) 18%, transparent)',
        color: 'var(--brand-primary)',
        fontWeight: 700,
        fontSize: 16,
        lineHeight: 1,
        letterSpacing: 0,
      }}
    >
      S
    </div>
  )
}

/* RecentRow / RecentsBlock removed: Récents is now a real Forge section in
   the sidebar's `sections` array (so it renders identically to "Naviguer"
   and "Avancé"). The hover-revealed delete button is injected via DOM
   after Forge renders the NavItems — see `useRecentDeleteOverlays` below. */

interface RecentRowProps {
  session: SessionSummary
  active: boolean
  onSelect: () => void
  onDelete: () => void
}

/**
 * One row in the Récents block: clickable label that loads the session,
 * with a hover-revealed delete IconButton. NavItem doesn't expose a
 * trailing-action slot, so the recents live in the sidebar's `footerContent`
 * as a custom block built from Forge primitives. The `.stark-recent-row`
 * stylesheet (injected once per app) drives the hover reveal — opacity is
 * the only thing toggled, the trash button stays in the DOM so its tooltip
 * and focus ring still work.
 */
function RecentRow({ session, active, onSelect, onDelete }: RecentRowProps) {
  return (
    <div
      className="stark-recent-row"
      data-active={active ? '1' : undefined}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
      role="button"
      tabIndex={0}
    >
      <span className="stark-recent-row-icon">
        <Chat20Regular />
      </span>
      <Text
        as="span"
        size="sm"
        weight={active ? 'medium' : 'normal'}
        truncate
        className="stark-recent-row-text"
        style={{ flex: 1, minWidth: 0, color: 'inherit' }}
      >
        {cleanSessionTitle(session)}
      </Text>
      <span
        className="stark-recent-row-action"
        onClick={(e) => e.stopPropagation()}
      >
        <IconButton
          icon={<Delete16Regular />}
          tooltip="Supprimer cette session"
          aria-label={`Supprimer la session ${cleanSessionTitle(session)}`}
          variant="ghost"
          size="xs"
          onClick={onDelete}
        />
      </span>
    </div>
  )
}

interface RecentsBlockProps {
  recents: SessionSummary[]
  activeSessionId: string | null
  onSelect: (sessionId: string) => void
  onDelete: (session: SessionSummary) => void
}

function RecentsBlock({ recents, activeSessionId, onSelect, onDelete }: RecentsBlockProps) {
  if (recents.length === 0) return null
  return (
    <div className="stark-recents-block">
      <div className="stark-recents-title">Récents</div>
      <div className="stark-recent-rows">
        {recents.map((s) => (
          <RecentRow
            key={s.session_id}
            session={s}
            active={s.session_id === activeSessionId}
            onSelect={() => onSelect(s.session_id)}
            onDelete={() => onDelete(s)}
          />
        ))}
      </div>
    </div>
  )
}

// Tiny global stylesheet for the delete-overlay buttons that the
// `useRecentDeleteOverlays` effect injects on top of Forge NavItems.
// Hover/focus reveals the trash; the title fades under it via mask-image
// so glyphs don't sit beneath the icon.
if (typeof document !== 'undefined' && !document.getElementById('stark-nav-delete-styles')) {
  const styleEl = document.createElement('style')
  styleEl.id = 'stark-nav-delete-styles'
  styleEl.textContent = `
    /* Recent NavItems: force single-line ellipsis truncation. The label
       sits in the second <span> child of the button (Forge renders icon
       span first, label span second). flex:1+min-width:0 lets the parent
       flex actually shrink the span so ellipsis kicks in. */
    button[data-stark-recent] {
      min-width: 0;
    }
    button[data-stark-recent] > span:nth-child(2) {
      flex: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
    }
    .stark-nav-row-delete {
      position: absolute;
      top: 50%;
      right: 8px;
      transform: translateY(-50%);
      width: 28px;
      height: 28px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 0;
      border-radius: var(--radius-sm);
      background: transparent;
      color: var(--text-secondary);
      cursor: pointer;
      opacity: 0;
      transition: opacity 120ms ease, background-color 120ms ease, color 120ms ease;
      z-index: 2;
    }
    .stark-nav-row-host:hover .stark-nav-row-delete,
    .stark-nav-row-host:focus-within .stark-nav-row-delete {
      opacity: 1;
    }
    .stark-nav-row-delete:hover {
      background-color: var(--bg-elevated);
      color: var(--color-error, #ef4444);
    }
    .stark-nav-row-host:hover button[data-stark-recent] > span:nth-child(2),
    .stark-nav-row-host:focus-within button[data-stark-recent] > span:nth-child(2) {
      mask-image: linear-gradient(to right, black calc(100% - 32px), transparent);
      -webkit-mask-image: linear-gradient(to right, black calc(100% - 32px), transparent);
    }
    @media (hover: none) {
      .stark-nav-row-delete { opacity: 1; }
    }
  `
  document.head.appendChild(styleEl)
}

/**
 * Incognito (Webba secure mode) palette override. Keeps the same brand
 * accent as normal mode but pushes every surface to near-black so the user
 * can SEE they switched modes at a glance without losing readability. The
 * persistent sticky Banner adds the explicit textual cue.
 */
if (typeof document !== 'undefined' && !document.getElementById('stark-incognito-styles')) {
  const styleEl = document.createElement('style')
  styleEl.id = 'stark-incognito-styles'
  styleEl.textContent = `
    [data-incognito="true"] {
      --bg-primary: #000000;
      --bg-secondary: #060606;
      --bg-tertiary: #0c0c0c;
      --bg-dropdown: #0c0c0c;
      --bg-elevated: #0e0e0e;
      --bg-subtle: rgba(255, 255, 255, 0.02);
      --border-color: #1a1a1a;
      --border-subtle: #111111;
    }
  `
  document.head.appendChild(styleEl)
}

export function AppShell({ children }: AppShellProps) {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const {
    reset: resetChat,
    loadSession,
    sessionId: activeChatSessionId,
  } = useChatContext()
  const { incognito } = useIncognito()
  const [navOpen, setNavOpen] = useState(false)
  const [collapsed, setCollapsed] = useState<boolean>(() => loadCollapsed())
  const [pendingDelete, setPendingDelete] = useState<SessionSummary | null>(null)
  // Toggle for the "Plus" sub-section. AppSidebar's NavItem schema doesn't
  // model a collapse-in-place sub-tree (children render as a tree, not as a
  // sibling section), so we drive sub-menu visibility from local state and
  // conditionally append the "Avancé" section to the sidebar's `sections`.
  const [showAdvanced, setShowAdvanced] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (collapsed) window.localStorage.setItem(COLLAPSED_KEY, '1')
    else window.localStorage.removeItem(COLLAPSED_KEY)
  }, [collapsed])

  const activeId = useMemo(() => activeIdFromPath(location.pathname), [location.pathname])

  // 15 entries is the desktop sweet spot: with 32px row height the Récents
  // block fills the post-nav whitespace down to the viewport bottom on a
  // 1080p screen without forcing the sidebar to scroll.
  const recentsQuery = useRecentSessions(15)
  const recents = recentsQuery.data ?? []

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSession(id),
    onSuccess: (_void, id) => {
      // Refresh the recents block AND the full Sessions page list.
      void queryClient.invalidateQueries({ queryKey: ['sessions'] })
      // If the deleted session is the one currently loaded into chat, clear
      // the surface so the user isn't reading a session they just removed.
      if (activeChatSessionId === id) resetChat()
    },
  })

  // Highlight the recent currently loaded into the chat (or the one being
  // viewed on /sessions/:id). Falls back to the path-derived activeId.
  const activeRecentSessionId = useMemo<string | null>(() => {
    const detail = location.pathname.match(/^\/sessions\/([^/]+)/)
    if (detail) return detail[1] ?? null
    if (location.pathname === '/' && activeChatSessionId) {
      const inList = recents.some((s) => s.session_id === activeChatSessionId)
      if (inList) return activeChatSessionId
    }
    return null
  }, [location.pathname, activeChatSessionId, recents])

  const sections: NavSection[] = useMemo(() => {
    const base: NavSection[] = [
      {
        title: 'Naviguer',
        items: [
          { id: 'memories', label: 'Mémoires', icon: <Brain20Regular /> },
          { id: 'tasks', label: 'Tâches', icon: <TaskListSquareLtr20Regular /> },
          {
            id: 'more',
            label: showAdvanced ? 'Moins' : 'Plus',
            icon: showAdvanced ? <ChevronUp20Regular /> : <ChevronDown20Regular />,
          },
        ],
      },
    ]
    if (showAdvanced) {
      base.push({
        title: 'Avancé',
        items: [
          { id: 'sessions', label: 'Sessions', icon: <History20Regular /> },
          { id: 'connecteurs', label: 'Connecteurs', icon: <PlugConnected20Regular /> },
          { id: 'devices', label: 'Appareils', icon: <Laptop20Regular /> },
          { id: 'traces', label: 'Traces', icon: <Pulse20Regular /> },
        ],
      })
    }
    if (recents.length > 0) {
      base.push({
        title: 'Récents',
        items: recents.map((s) => ({
          id: `recent-${s.session_id}`,
          label: cleanSessionTitle(s),
          icon: <Chat20Regular />,
        })),
      })
    }
    return base
  }, [showAdvanced, recents])

  // No bottomItems: Réglages now lives inside the user avatar dropdown
  // (top-right) so the sidebar stays focused on conversation surfaces and
  // navigation. Less duplication, cleaner footer for the recents block.

  const loadRecent = (sid: string): void => {
    setNavOpen(false)
    // Hydrate the session into the chat surface so the user can continue it
    // in place, instead of being shipped off to a read-only detail page.
    if (location.pathname !== '/') navigate('/')
    void loadSession(sid).catch(() => {
      // If the load fails (network, deleted, perms) drop back to a clean
      // chat instead of leaving the user staring at the previous session.
      resetChat()
    })
  }

  const handleNewChat = (): void => {
    setNavOpen(false)
    // Always start fresh: drop the in-flight stream, clear local history,
    // forget the session id so the next message starts a new server-side
    // conversation. Then make sure we're on the chat route.
    resetChat()
    if (location.pathname !== '/') navigate('/')
  }

  const handleNavigate = (id: string): void => {
    if (id === 'more') {
      // Pseudo-nav: this item is a sub-menu toggle, not a route. Keep the
      // drawer open on mobile so the user sees the new section appear.
      setShowAdvanced((p) => !p)
      return
    }
    if (id.startsWith('recent-')) {
      const sid = id.slice('recent-'.length)
      loadRecent(sid)
      return
    }
    setNavOpen(false)
    if (id === 'new-chat') {
      handleNewChat()
      return
    }
    const path = NAV_PATHS[id]
    if (path) navigate(path)
  }

  // Refs for the desktop and mobile sidebar containers. Used by the delete
  // overlay injection effect below to scope the DOM walk.
  const desktopSidebarRef = useRef<HTMLDivElement | null>(null)
  const mobileSidebarRef = useRef<HTMLDivElement | null>(null)

  /**
   * Forge AppSidebar renders NavItems as <button> elements with no data
   * attributes we can target. To attach an absolute delete button per
   * "recent-{id}" item, we walk the sidebar buttons after each render,
   * match by label text, mark them, and append a real <button> overlay
   * sized + positioned over the right edge of the parent <div>. Click
   * stopPropagation so loading the session doesn't fire when the user
   * clicks the trash. The overlay is opacity 0 by default and reveals
   * on .stark-nav-row-host:hover via the global stylesheet.
   */
  useLayoutEffect(() => {
    const containers = [desktopSidebarRef.current, mobileSidebarRef.current].filter(
      (x): x is HTMLDivElement => x !== null,
    )
    if (containers.length === 0 || recents.length === 0) return

    const labelToSession = new Map<string, SessionSummary>()
    for (const s of recents) labelToSession.set(cleanSessionTitle(s), s)

    const created: HTMLButtonElement[] = []

    for (const container of containers) {
      const buttons = container.querySelectorAll<HTMLButtonElement>('button')
      for (const btn of Array.from(buttons)) {
        const txt = btn.textContent?.trim() ?? ''
        const session = labelToSession.get(txt)
        if (!session) continue
        if (btn.dataset.starkRecent === session.session_id) continue
        btn.dataset.starkRecent = session.session_id

        const host = btn.parentElement
        if (!host) continue
        host.classList.add('stark-nav-row-host')
        if (getComputedStyle(host).position === 'static') {
          host.style.position = 'relative'
        }

        const overlay = document.createElement('button')
        overlay.type = 'button'
        overlay.className = 'stark-nav-row-delete'
        overlay.setAttribute('aria-label', `Supprimer la session ${txt}`)
        overlay.title = 'Supprimer'
        overlay.innerHTML =
          '<svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 5h13M8 5V3.5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1V5M5.5 5l.7 11A1.6 1.6 0 0 0 7.8 17.5h4.4a1.6 1.6 0 0 0 1.6-1.5l.7-11M8.5 8.5v5M11.5 8.5v5"/></svg>'
        overlay.addEventListener('click', (ev) => {
          ev.stopPropagation()
          ev.preventDefault()
          setPendingDelete(session)
        })
        host.appendChild(overlay)
        created.push(overlay)
      }
    }

    return () => {
      for (const el of created) el.remove()
    }
  }, [recents, sections, collapsed])

  // "Nouveau chat" rendered as a primary Button instead of a NavItem so it
  // pops out of the section list and reads as a primary action. Lives in
  // `headerContent` directly under the brand. When collapsed, the
  // headerContent slot is hidden by AppSidebar, so we mount the icon-only
  // version separately on the rail (not strictly possible without forking
  // AppSidebar; the rail still has the brand mark + collapse arrow + nav
  // icons, so users still have an entry point via the chat icons).
  const newChatHeader = (
    <Button
      variant="primary"
      size="md"
      icon={<ChatAdd20Regular />}
      fullWidth
      onClick={handleNewChat}
    >
      Nouveau chat
    </Button>
  )

  // Mobile bottom-nav: keep "Nouveau chat" as the leftmost slot for instant
  // access since the sidebar is hidden behind a hamburger. We synthesize the
  // entry locally because it is no longer part of the `sections` array.
  const mobileItems = useMemo<Array<{ id: string; label: string; icon: ReactNode }>>(
    () => [
      { id: 'new-chat', label: 'Nouveau', icon: <ChatAdd20Regular /> },
      { id: 'memories', label: 'Mémoires', icon: <Brain20Regular /> },
      { id: 'tasks', label: 'Tâches', icon: <TaskListSquareLtr20Regular /> },
      { id: 'devices', label: 'Appareils', icon: <Laptop20Regular /> },
    ],
    [],
  )

  return (
    <div
      data-incognito={incognito ? 'true' : undefined}
      style={{
        display: 'flex',
        minHeight: '100dvh',
        backgroundColor: 'var(--bg-primary)',
        color: 'var(--text-primary)',
      }}
    >
      {!isMobile && (
        <div
          ref={desktopSidebarRef}
          style={{
            position: 'sticky',
            top: 0,
            height: '100dvh',
            alignSelf: 'flex-start',
            flexShrink: 0,
            display: 'flex',
          }}
        >
          <AppSidebar
            sections={sections}
            value={activeRecentSessionId ? `recent-${activeRecentSessionId}` : activeId}
            onNavigate={handleNavigate}
            logo={collapsed ? null : <HeaderBrand />}
            compactLogo={null}
            headerContent={collapsed ? null : newChatHeader}
            collapsible
            collapsed={collapsed}
            onCollapsedChange={setCollapsed}
            width={220}
            height="100%"
            showSearch={false}
            forceDesktop
          />
        </div>
      )}

      {isMobile && (
        <div ref={mobileSidebarRef}>
          <AppSidebar
            mode="drawer"
            open={navOpen}
            onClose={() => setNavOpen(false)}
            position="left"
            sections={sections}
            value={activeRecentSessionId ? `recent-${activeRecentSessionId}` : activeId}
            onNavigate={handleNavigate}
            logo={<HeaderBrand />}
            compactLogo={null}
            headerContent={newChatHeader}
            showSearch={false}
          />
        </div>
      )}

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: isMobile ? 80 : 0,
          position: 'relative',
        }}
      >
        {/* Incognito banner removed. The dark palette + the composer mode
            picker carry the signal. A persistent yellow strip on every page
            was overkill once the visual theme changed. */}

        {isMobile && (
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 9,
              padding: 'var(--spacing-sm) var(--spacing-md)',
              backgroundColor: 'var(--bg-primary)',
            }}
          >
            <HStack justify="between" align="center" gap="sm">
              <HStack gap="sm" align="center">
                <IconButton
                  icon={<Navigation20Regular />}
                  tooltip="Menu"
                  aria-label="Ouvrir le menu"
                  variant="ghost"
                  size="sm"
                  onClick={() => setNavOpen(true)}
                />
                <HeaderBrand />
              </HStack>
            </HStack>
          </div>
        )}

        {/* Profile menu floats top-right above the page content. Same anchor
            on desktop and mobile so the user always knows where to find it.
            Larger inset so the avatar reads as an actual margin from the
            viewport corner instead of looking glued to the edge. */}
        <div
          style={{
            position: 'absolute',
            top: 'var(--spacing-lg)',
            right: 'var(--spacing-lg)',
            zIndex: 10,
          }}
        >
          <UserMenu />
        </div>

        {/* Pages other than chat get a generous top inset so content never
            butts up against the viewport edge. Chat owns its own full-height
            layout (welcome center + sticky composer) and is excluded. */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            paddingTop: location.pathname === '/' ? 0 : 64,
          }}
        >
          {children}
        </div>
      </div>

      {isMobile && (
        <BottomNav
          variant="fixed"
          items={mobileItems}
          value={activeId}
          onNavigate={handleNavigate}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return
          deleteMutation.mutate(pendingDelete.session_id)
          setPendingDelete(null)
        }}
        title="Supprimer cette session ?"
        description={
          pendingDelete
            ? `« ${cleanSessionTitle(pendingDelete)} » sera retirée de l'historique. Cette action est définitive.`
            : ''
        }
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
  )
}
